import { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "../../../shadcn/components/ui/button";
import { amountFormatter, getAuctionEventsWithPagination } from "services";
import { ColumnDef } from "@tanstack/react-table";
import { useSelector } from "react-redux";
import CalendarDotsIcon from "../../../icons/CalendarDotsIcon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../shadcn/components/ui/popover";
import ChevronUpSvg from "../../../icons/ChevronUpSvg";
import ChevronDownSvg from "../../../icons/ChevronDownSvg";
import { Calendar } from "../../../shadcn/components/ui/calendar";
import Pusher from "pusher-js";
import StatusDropdown from "../events/StatusDropDown";
import { useRouter } from "next/router";
import EventStatusTable from "./EventsStatusTable";
import { useEventsStatusQueue } from "../../../hoc/useEventsStatusQueue";

interface PropertyDump {
  asset?: {
    name: string;
  };
  sub_asset_type?: {
    name: string;
  };
}

export type tableData = {
  [x: string]: any;
  id: string;
};

interface EventStatusTableProps {
  outerContainerClass?: string;
  events: any;
  setEvents: any;
  eventsRef: any;
}

export default function EventStatusComponent({
  outerContainerClass,
  events,
  setEvents,
  eventsRef,
}: EventStatusTableProps) {
  const [isLoading, setIsLoading] = useState(false);

  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const router = useRouter();
  const eventQueueRef = useRef<any[]>([]);
  const statuses = [
    "All",
    "Cancelled",
    "End",
    "Pending",
    "Live",
    "Rejected",
    "Accepted",
    "Success",
    "Failed",
    "Pending_Bid_Approval",
    "Holding_Bid_Approval",
    "Bid_Rejected",
  ];
  const currentBranch = useSelector(
    (state: any) => state.bankerAuth.currentBranch,
  );

  useEventsStatusQueue<any>(setEvents, eventQueueRef, 200);

  useEffect(() => {
    const filteredData =
      selectedStatus && selectedStatus !== "All"
        ? events.filter(
            (record: any) => record.status === selectedStatus.toLowerCase(),
          )
        : events;

    setFilteredRecords(filteredData?.slice(0, 3));
  }, [events, selectedStatus]);

  const handleGetAuctionEvents = useCallback(
    async (shouldShowNextPage: boolean, status: string | null = null) => {
      if (isFetchingMore || (shouldShowNextPage && page >= totalPages)) return;

      setIsFetchingMore(true);
      const currentPage = shouldShowNextPage ? page + 1 : 1;

      const preparedPayload: any = {
        startDate: startDate
          ? new Date(
              Date.UTC(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate(),
                0,
                0,
                0,
                0,
              ),
            ).toISOString()
          : "",
        status: selectedStatus ? selectedStatus.toLowerCase() : "",
        page: currentPage,
      };

      try {
        if (!shouldShowNextPage) setIsLoading(true);
        const res: any = await getAuctionEventsWithPagination(preparedPayload);

        setTotalPages(res.meta.total_page);
        setPage(currentPage);

        setEvents((prev: any) =>
          shouldShowNextPage ? [...prev, ...res.data] : res.data,
        );

        if (currentPage < totalPages) {
          handleGetAuctionEvents(true, status);
        }
      } catch (error) {
        console.error("Error fetching auction events:", error);
      } finally {
        setIsLoading(false);
        setIsFetchingMore(false);
      }
    },
    [page, totalPages, startDate, isFetchingMore, selectedStatus],
  );

  useEffect(() => {
    if (page <= totalPages) {
      console.log("page->>", page);
      handleGetAuctionEvents(true);
    }
  }, [page]);

  useEffect(() => {
    handleGetAuctionEvents(false);
  }, [currentBranch, startDate]);

  useEffect(() => {
    Pusher.logToConsole = false;
    const pusher = new Pusher(`${process.env.NEXT_PUBLIC_PUSHER_KEY}`, {
      cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER,
    });

    const channelId = `update-dashboard`;
    const channel = pusher.subscribe(channelId);

    channel.bind("auction_event_has_started", (data: any) => {
      if (data) {
        eventQueueRef.current.push({
          uuid: data.event_uuid,
          newStatus: "live",
        });
      }
    });

    channel.bind("auction_event_has_ended", (data: any) => {
      if (data) {
        eventQueueRef.current.push({ uuid: data.event_uuid, newStatus: "end" });
      }
    });

    pusher.connection.bind("error", function (err: any) {
      if (err?.error?.data?.code === 4004) {
        console.log(">>> detected limit error");
      } else if (err?.data?.code === 1006) {
        // Websocket: CLOSED ABNORMALLY
      }
    });

    pusher.connection.bind("disconnected", function (err: any) {
      console.log(">>> disconnected");
    });

    pusher.connection.bind("connected", function (err: any) {
      console.log("connected", pusher.sessionID);
    });

    return function cleanup() {
      pusher.disconnect();
    };
  }, [currentBranch]);

  const FilterLeft = () => {
    const formatDate = (date: Date | undefined) => {
      if (!date) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    const [isStartDateOpen, setIsStartDateOpen] = useState(false);

    const handleStartDateSelect = (date: Date | undefined) => {
      setStartDate(date);
      setIsStartDateOpen(false);
    };

    const validateAndPrepareSearch = useCallback(() => {
      if (startDate) {
        return null;
      }
    }, [startDate]);

    useEffect(() => {
      validateAndPrepareSearch();
    }, [startDate, validateAndPrepareSearch]);

    return (
      <div className="flex space-x-5 items-center">
        <StatusDropdown
          statuses={statuses}
          selectedStatus={selectedStatus}
          onSelect={setSelectedStatus}
        />

        <Popover onOpenChange={setIsStartDateOpen} open={isStartDateOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="flex px-2 sm:px-4 gap-1 sm:gap-2 items-center w-full justify-between text-sm rounded-[8px] font-openSans text-brand-textLightGrey"
            >
              <CalendarDotsIcon className="w-3 h-3 sm:w-4 sm:h-4" />
              {startDate ? (
                <span className="font-openSans font-normal text-mobile-body-xsm leading-3 md:text-web-body-md md:leading-4">
                  {formatDate(startDate)}
                </span>
              ) : (
                <span className="font-openSans font-normal text-mobile-body-xsm leading-3 md:text-web-body-md md:leading-4">
                  Start date
                </span>
              )}
              <div className="px-1 lg:px-2">
                {isStartDateOpen ? (
                  <ChevronUpSvg width={14} height={14} fill={"#7D7D7D"} />
                ) : (
                  <ChevronDownSvg width={14} height={14} fill={"#7D7D7D"} />
                )}
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="bg-white w-fit">
            <div className="flex justify-end">
              <p
                onClick={() => {
                  setStartDate(undefined);
                }}
                className="text-mobile-body-sm hover:cursor-pointer"
              >
                Clear
              </p>
            </div>
            <div className="flex justify-center -translate-y-2">
              <p className="font-openSans text-base text-center font-semibold text-brand-textLightGrey ">
                Select Start date
              </p>
            </div>
            <Calendar
              className="bg-white"
              mode="single"
              selected={startDate}
              onSelect={handleStartDateSelect}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <p
          onClick={() => {
            router.push("/dashboard/events");
          }}
          className="hover:cursor-pointer  font-openSans font-semibold text-web-body-sm leading-4 text-brand-bgBlack underline text-nowrap"
        >
          View All
        </p>
      </div>
    );
  };

  const columns: ColumnDef<tableData>[] = [
    {
      accessorKey: "listing_id",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="text-xs px-0 min-w-14 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Event ID
        </Button>
      ),
      cell: ({ row }) => (
        <div className="lowercase min-w-16">{row.getValue("listing_id")}</div>
      ),
    },
    {
      accessorKey: "event_name",
      header: "Event Name",
      cell: ({ row }) => {
        return (
          <div className="flex items-center space-x-1 hover:cursor-pointer">
            <p className="font-openSans font-semibold text-web-body-sm leading-4 text-brand-bgBlack underline">
              {row.getValue("event_name")}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "reserve_price",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="text-xs px-0 min-w-20 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Reserve price
        </Button>
      ),
      cell: ({ row }) => {
        const amount = parseFloat(row.getValue("reserve_price"));
        return <div className="min-w-20">₹{amountFormatter(amount)}</div>;
      },
    },
    {
      accessorKey: "property_dump",
      header: () => <div className="">Property Type</div>,
      cell: ({ row }) => {
        const propertyDump = row.getValue("property_dump") as PropertyDump;
        return (
          <div className="">
            <span className="font-medium capitalize">
              {propertyDump?.asset?.name || ""}
            </span>
            <span className="font-medium capitalize ml-1">
              {propertyDump?.sub_asset_type?.name || ""}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <div
          className={`capitalize px-2 py-1 rounded-full text-center text-sm font-normal
    ${
      row.getValue("status") === "live"
        ? "bg-[#FF752C] text-white"
        : row.getValue("status") === "pending"
        ? "bg-yellow-100 text-yellow-700"
        : row.getValue("status") === "end"
        ? "bg-gray-100 text-gray-700"
        : row.getValue("status") === "cancelled"
        ? "bg-red-50 text-red-500"
        : row.getValue("status") === "accepted"
        ? "bg-green-50 text-green-500"
        : "bg-gray-200 text-gray-800"
    }
  `}
        >
          {row.getValue("status")}
        </div>
      ),
    },
  ];

  const handleUpdateEventStatus = async (
    eventUuid: string,
    newStatus: string,
  ) => {
    try {
      const tempData: any = eventsRef?.current.map((record: any) => {
        if (record.uuid === eventUuid) {
          return { ...record, status: newStatus };
        } else {
          return record;
        }
      });

      setEvents(tempData);
    } catch (err) {
      console.log("error", err);
    }
  };

  return (
    <div className={`${outerContainerClass}`}>
      <EventStatusTable
        data={filteredRecords}
        columns={columns}
        filterComponent={<FilterLeft />}
        loading={isLoading}
      />
    </div>
  );
}
